import React, { useState, useEffect } from "react";
import Welcome from "./Welcome";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaShoppingBag,
  FaBars,
  FaTimes,
  FaHome,
  FaStore,
  FaEnvelope,
  FaBoxOpen,
  FaSignOutAlt,
  FaUser,
  FaCoins,
} from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../features/auth/authSlice";

function Header() {
  const { user } = useSelector((state) => state.auth);
  const cartItems = user?.data?.cart || [];
  const balance = user?.data?.balance || 0;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect for header - throttled for performance
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [mobileMenuOpen]);

  const onLogOut = () => {
    dispatch(logout());
    setMobileMenuOpen(false);
    setTimeout(() => {
      navigate("login");
    }, 200);
  };

  const navLinks = [
    { name: "Home", path: "/", icon: <FaHome /> },
    { name: "Catalog", path: "/catalog", icon: <FaStore /> },
    { name: "Contact", path: "/contact", icon: <FaEnvelope /> },
  ];

  if (user) {
    navLinks.push({
      name: "My Orders",
      path: "/my-orders",
      icon: <FaBoxOpen />,
    });
    navLinks.push({
      name: "Credits",
      path: "/credits",
      icon: <FaCoins />,
    });
    if (user?.data?.role === "admin") {
      navLinks.push({
        name: "Admin Matrix",
        path: "/admin",
        icon: <FaUser />,
      });
    }
  }

  return (
    <>
      <Welcome />
      <header
        className={`w-full sticky top-0 z-40 transition-all duration-500 border-b ${
          scrolled || location.pathname !== "/"
            ? "bg-black/80 backdrop-blur-xl border-white/10 shadow-[0_0_20px_rgba(0,242,255,0.1)] py-4"
            : "bg-transparent border-transparent py-6"
        }`}
        style={{
          willChange: 'transform, background-color',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
        }}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 group relative z-50"
          >
            <div className="w-9 h-9 sm:w-11 sm:h-11 bg-gradient-to-tr from-cyan-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-[0_0_15px_rgba(0,242,255,0.4)] group-hover:rotate-[360deg] transition-all duration-700">
              <FaShoppingBag className="text-sm sm:text-xl" />
            </div>
            <span className="font-black text-xl sm:text-2xl text-white tracking-widest uppercase italic">
              Gamer
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500 ml-1">
                Hub
              </span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10 px-8 py-2.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-lg">
            {navLinks.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all relative group py-1 ${
                  location.pathname === item.path
                    ? "text-cyan-400"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {item.name}
                <span
                  className={`absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-400 to-purple-500 transform origin-left transition-transform duration-300 ${
                    location.pathname === item.path
                      ? "scale-x-100 shadow-[0_0_10px_#00f2ff]"
                      : "scale-x-0 group-hover:scale-x-100"
                  }`}
                ></span>
              </Link>
            ))}
          </div>

          {/* Icons & Actions */}
          <div className="flex items-center gap-4 z-50">
            <Link
              to="/search"
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 hover:border-cyan-400/50 transition-all text-gray-400 hover:text-cyan-400 hover:shadow-[0_0_10px_rgba(0,242,255,0.2)]"
            >
              <FaSearch className="text-lg" />
            </Link>

            <Link
              to="/cart"
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 hover:border-purple-400/50 transition-all text-gray-400 hover:text-purple-400 relative hover:shadow-[0_0_10px_rgba(188,19,254,0.2)]"
            >
              <FaShoppingBag className="text-lg" />
              {cartItems?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-cyan-500 text-black text-[10px] font-black h-5 w-5 rounded-lg flex items-center justify-center ring-2 ring-black animate-pulse">
                  {cartItems.length}
                </span>
              )}
            </Link>

            {/* Credits Balance */}
            {user && (
              <Link
                to="/credits"
                className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-900/50 to-blue-900/50 border border-purple-500/30 rounded-xl hover:border-purple-500 transition-all"
              >
                <FaCoins className="text-purple-400 animate-bounce" />
                <span className="text-xs font-black text-purple-100 tracking-wider">
                  {balance.toLocaleString()} Pts
                </span>
              </Link>
            )}

            <div className="h-6 w-[1px] bg-white/10 mx-1 hidden lg:block"></div>

            {user ? (
              <div className="hidden lg:flex items-center gap-3 pl-2 group">
                <div className="flex flex-col items-end">
                  <h3 className="text-xl font-black text-white uppercase tracking-widest italic">
                    Commander
                  </h3>
                  <span className="text-[10px] font-black text-white/90 uppercase tracking-widest">
                    {user?.data?.name || "Player"}
                  </span>
                  <button
                    onClick={onLogOut}
                    className="text-[9px] font-black text-red-400 uppercase tracking-tighter hover:text-red-300 flex items-center gap-1 mt-1 transition-colors"
                  >
                    Logout <FaSignOutAlt />
                  </button>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400/20 to-purple-500/20 border border-white/10 group-hover:border-cyan-400/50 transition-all flex items-center justify-center text-cyan-400 font-black shadow-inner">
                  {(user?.data?.name?.[0] || "P").toUpperCase()}
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden lg:flex bg-gradient-to-r from-cyan-500 to-blue-600 text-black px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-[0_0_15px_rgba(0,242,255,0.3)] items-center gap-2"
              >
                <FaUser className="text-[10px]" /> Login
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white"
              onClick={() => setMobileMenuOpen(true)}
            >
              <FaBars className="text-xl" />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[900000] lg:hidden transition-opacity duration-300 ${
          mobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        ></div>

        {/* Drawer */}
        <div
          className={`absolute top-0 right-0 w-[320px] h-full bg-[#0b0e14] border-l border-white/10 shadow-2xl transform transition-transform duration-500 flex flex-col  ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Drawer Header */}
          <div className="p-8 flex items-center justify-between border-b border-white/5 bg-black/40">
            <span className="font-black text-xl text-white tracking-[0.2em] uppercase italic">
              Terminal
            </span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-red-400 transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 overflow-y-auto p-8 flex flex-col gap-3">
            {navLinks.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-5 p-5 rounded-2xl border transition-all ${
                  location.pathname === item.path
                    ? "bg-cyan-500/10 border-cyan-500/50 text-cyan-400 shadow-[0_0_15px_rgba(0,242,255,0.1)]"
                    : "bg-white/5 border-transparent text-gray-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <span
                  className={`text-2xl ${
                    location.pathname === item.path
                      ? "text-cyan-400 drop-shadow-[0_0_5px_#00f2ff]"
                      : "text-gray-600"
                  }`}
                >
                  {item.icon}
                </span>
                <span className="font-black uppercase tracking-widest text-xs">
                  {item.name}
                </span>
              </Link>
            ))}
          </div>

          {/* Drawer Footer */}
          <div className="p-8 border-t border-white/5 bg-black/60">
            {user ? (
              <div className="space-y-6">
                <div className="flex items-center gap-4 px-2">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-600/20 border border-white/10 flex items-center justify-center text-cyan-400 font-black shadow-inner">
                    {(user?.data?.name?.[0] || "P").toUpperCase()}
                  </div>
                  <div>
                    <p className="font-black text-white text-sm uppercase tracking-wider">
                      {user?.data?.name}
                    </p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase truncate max-w-[150px]">
                      {user?.data?.email}
                    </p>
                  </div>
                </div>
                <Link
                  to="/credits"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between w-full bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/30 text-purple-100 font-black py-4 px-6 rounded-2xl hover:border-purple-500 transition-all group"
                >
                  <span className="flex items-center gap-3 text-xs uppercase tracking-widest">
                    <FaCoins className="text-purple-400 group-hover:rotate-12 transition-transform" />{" "}
                    Gamer Pts
                  </span>
                  <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-lg text-[10px] font-black">
                    {balance.toLocaleString()}
                  </span>
                </Link>
                <button
                  onClick={onLogOut}
                  className="w-full bg-red-500/10 border border-red-500/30 text-red-500 font-black py-4 rounded-2xl hover:bg-red-500 hover:text-black transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-widest"
                >
                  <FaSignOutAlt /> Terminate Session
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-black py-5 rounded-2xl hover:brightness-110 transition-all flex items-center justify-center gap-3 shadow-[0_0_20px_rgba(0,242,255,0.2)] text-xs uppercase tracking-[0.2em]"
              >
                <FaUser /> Join Hardware Raid
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
